import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
const MapPage = lazy(() => import("./pages/Map"));
const Survey = lazy(() => import("./pages/Survey"));
import ErrorBoundary from "@/components/ErrorBoundary";
import { MunicipalityProvider, useMunicipalityContext } from "@/contexts/MunicipalityContext";
import { URLConfigProvider, useURLConfigContext } from "@/contexts/URLConfigContext";
import { Screensaver } from "@/components/Screensaver";
import { useInactivity } from "@/hooks/useInactivity";
import { AndroidOptimizer } from "@/components/AndroidOptimizer";
import { useMemoryMonitor } from "@/hooks/useMemoryMonitor";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { setLoggerMunicipalityId, logToRemote } from "@/utils/remoteLogger";

import { TouchCoordinateFixer } from "@/components/TouchCoordinateFixer";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: 1000,
      staleTime: 2 * 60 * 1000, // 2 minutos (reducido para memoria)
      gcTime: 5 * 60 * 1000, // 5 minutos (reducido)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

// Limpieza periódica de caché cada 5 minutos
setInterval(() => {
  const now = Date.now();
  queryClient.getQueryCache().getAll().forEach((query) => {
    const dataUpdatedAt = query.state.dataUpdatedAt;
    if (dataUpdatedAt && now - dataUpdatedAt > 5 * 60 * 1000) {
      queryClient.removeQueries({ queryKey: query.queryKey });
    }
  });
}, 5 * 60 * 1000);

// Array de eventos de inactividad fuera del componente para mantener referencia estable
const INACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'];
const DISPLAY_ROUTES = ['/', '/map', '/survey'];

// Componente interno que muestra loading o contenido
const AppLoader = () => {
  const config = useURLConfigContext();

  if (config.isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return <AppContent />;
};

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useURLConfigContext();
  const { municipalityId, setMunicipalityId } = useMunicipalityContext();
  const [isWebViewOpen, setIsWebViewOpen] = useState(false);

  // Dynamic color variables application
  useEffect(() => {
    if (config.primaryColor) {
      document.documentElement.style.setProperty('--primary-color', config.primaryColor);
    }
    if (config.backgroundColor) {
      document.documentElement.style.setProperty('--background-color', config.backgroundColor);
      document.body.style.backgroundColor = config.backgroundColor;
    }
    
    // Dynamic background SVG pattern based on primary color
    const primColorHex = (config.primaryColor || '#005041').replace('#', '');
    const svgUrl = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><g opacity="0.15"><path d="M20 30 Q 15 35 20 40 Q 25 35 20 30 M 20 40 L 20 48" stroke="%23${primColorHex}" fill="none" stroke-width="2"/><ellipse cx="16" cy="36" rx="4" ry="6" fill="%23${primColorHex}" opacity="0.4"/><ellipse cx="24" cy="36" rx="4" ry="6" fill="%23${primColorHex}" opacity="0.4"/><path d="M60 10 Q 55 15 60 20 Q 65 15 60 10 M 60 20 L 60 28" stroke="%23${primColorHex}" fill="none" stroke-width="2"/><ellipse cx="56" cy="16" rx="4" ry="6" fill="%23${primColorHex}" opacity="0.4"/><ellipse cx="64" cy="16" rx="4" ry="6" fill="%23${primColorHex}" opacity="0.4"/><path d="M40 60 Q 35 65 40 70 Q 45 65 40 60 M 40 70 L 40 78" stroke="%23${primColorHex}" fill="none" stroke-width="2"/><ellipse cx="36" cy="66" rx="4" ry="6" fill="%23${primColorHex}" opacity="0.4"/><ellipse cx="44" cy="66" rx="4" ry="6" fill="%23${primColorHex}" opacity="0.4"/></g></svg>')`;
    document.body.style.backgroundImage = svgUrl;
  }, [config.primaryColor, config.backgroundColor]);

  // Monitoring hooks
  usePerformanceMonitor();

  const memoryStats = useMemoryMonitor({
    checkInterval: 30000,
    warningThreshold: 0.75,
    criticalThreshold: 0.85,
    onWarning: (stats) => {
      logToRemote('memory_warning', {
        usagePercent: Math.round(stats.usagePercent * 100),
        usedMB: Math.round(stats.usedJSHeapSize / 1024 / 1024),
        limitMB: Math.round(stats.jsHeapSizeLimit / 1024 / 1024)
      }, 'warn');
    },
    onCritical: (stats) => {
      logToRemote('memory_critical', {
        usagePercent: Math.round(stats.usagePercent * 100),
        usedMB: Math.round(stats.usedJSHeapSize / 1024 / 1024),
        limitMB: Math.round(stats.jsHeapSizeLimit / 1024 / 1024)
      }, 'critical');

      // Limpiar caché agresivamente
      console.warn('🧹 Limpiando caché por memoria crítica...');
      queryClient.clear();
    },
    enabled: true
  });

  useEffect(() => {
    if (config.municipalityId) {
      setMunicipalityId(config.municipalityId);
    }
  }, [config.municipalityId, setMunicipalityId]);

  // Configurar municipality ID para el logger y loggear app started
  useEffect(() => {
    if (municipalityId) {
      setLoggerMunicipalityId(municipalityId);
      logToRemote('app_started', {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        memory: (performance as any).memory ? {
          limitMB: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024)
        } : 'not_available'
      }, 'info');
    }
  }, [municipalityId]);

  const shouldActivateScreensaver =
    location.pathname === '/' && config.screensaverEnabled;
  const shouldReturnHome =
    !config.screensaverEnabled &&
    (isWebViewOpen || (
      location.pathname !== '/' &&
      DISPLAY_ROUTES.includes(location.pathname)
    ));

  useEffect(() => {
    const handleWebViewOpen = () => {
      console.log('🌐 WebView abierto - deshabilitando useInactivity');
      setIsWebViewOpen(true);
    };
    const handleWebViewClose = () => {
      console.log('🌐 WebView cerrado - habilitando useInactivity');
      setIsWebViewOpen(false);
    };
    const handleForceScreensaver = () => {
      if (!config.screensaverEnabled) return;
      console.log('💤 Forzando activación del salvapantallas');
      setScreensaverActive(true);
    };

    window.addEventListener('webViewOpen', handleWebViewOpen);
    window.addEventListener('webViewClose', handleWebViewClose);
    window.addEventListener('forceScreensaver', handleForceScreensaver);

    return () => {
      window.removeEventListener('webViewOpen', handleWebViewOpen);
      window.removeEventListener('webViewClose', handleWebViewClose);
      window.removeEventListener('forceScreensaver', handleForceScreensaver);
    };
  }, [config.screensaverEnabled]);

  const { isInactive, resetInactivity } = useInactivity({
    timeout: config.screensaverTimeout,
    events: INACTIVITY_EVENTS,
    enabled: isWebViewOpen || shouldReturnHome || shouldActivateScreensaver,
  });
  const [screensaverActive, setScreensaverActive] = useState(false);
  const [isClosingScreensaver, setIsClosingScreensaver] = useState(false);

  useEffect(() => {
    const handleWebViewActivity = () => resetInactivity();
    window.addEventListener('webViewActivity', handleWebViewActivity);
    return () => window.removeEventListener('webViewActivity', handleWebViewActivity);
  }, [resetInactivity]);

  useEffect(() => {
    if (shouldActivateScreensaver) {
      setScreensaverActive(isInactive);
    } else {
      setScreensaverActive(false);
    }
  }, [isInactive, shouldActivateScreensaver]);

  useEffect(() => {
    if (!isInactive || !shouldReturnHome) return;

    window.dispatchEvent(new Event('closeWebView'));
    navigate({ pathname: '/', search: window.location.search });
    resetInactivity();
  }, [isInactive, shouldReturnHome, navigate, resetInactivity]);

  useEffect(() => {
    if (!isInactive || !isWebViewOpen || !config.screensaverEnabled) return;

    window.dispatchEvent(new Event('closeWebView'));
    navigate({ pathname: '/', search: window.location.search });
  }, [isInactive, isWebViewOpen, config.screensaverEnabled, navigate]);

  const handleScreensaverClose = () => {
    setIsClosingScreensaver(true);

    // Cerrar cualquier WebView abierto
    window.dispatchEvent(new Event('closeWebView'));

    // Navegar a inicio preservando los query params existentes
    navigate({ pathname: '/', search: window.location.search });

    setTimeout(() => {
      setScreensaverActive(false);
      resetInactivity();
      setIsClosingScreensaver(false);
    }, 300);
  };

  return (
    <div className={screensaverActive || isClosingScreensaver ? 'pointer-events-blocked' : ''}>
      <AndroidOptimizer />
      <TouchCoordinateFixer />
      <Toaster />
      <Sonner />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/map" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">Cargando mapa...</div>}>
            <MapPage />
          </Suspense>
        } />
        <Route path="/survey" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">Cargando encuesta...</div>}>
            <Survey />
          </Suspense>
        } />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {shouldActivateScreensaver && (
        <Screensaver
          municipalityId={municipalityId}
          isActive={screensaverActive}
          onInteraction={handleScreensaverClose}
        />
      )}
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <URLConfigProvider>
        <MunicipalityProvider>
          <TooltipProvider>
            <BrowserRouter>
              <AppLoader />
            </BrowserRouter>
          </TooltipProvider>
        </MunicipalityProvider>
      </URLConfigProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
